/**
 * SEO Schema Utilities
 *
 * Builds structured data (JSON-LD) for enhanced search results.
 * All functions are guarded by SEO_SCHEMA_ENABLED env flag.
 */

/**
 * Check if schema generation is enabled via environment variable
 */
export const isSchemaEnabled = () => {
  // Default to true if not explicitly set to 'false'
  const enabled = process.env.REACT_APP_SEO_SCHEMA_ENABLED;
  return enabled !== 'false';
};

/**
 * Extract first N sentences from HTML/Markdown text, stripping formatting
 * @param {string} content - HTML or Markdown content
 * @param {number} maxChars - Maximum character length
 * @returns {string} Plain text excerpt
 */
export const extractFirstSentences = (content, maxChars = 400) => {
  if (!content) return '';

  // Strip HTML tags
  const withoutHtml = content.replace(/<[^>]*>/g, '');

  // Strip markdown formatting
  const withoutMarkdown = withoutHtml
    .replace(/[#*_`~\[\]]/g, '') // Remove markdown symbols
    .replace(/!\[.*?\]\(.*?\)/g, '') // Remove image markdown
    .replace(/\[.*?\]\(.*?\)/g, '$1'); // Remove link markdown but keep text

  // Get first paragraph or first few sentences
  const firstParagraph = withoutMarkdown.split('\n\n')[0] || withoutMarkdown;

  // Trim to maxChars at sentence boundary
  if (firstParagraph.length <= maxChars) {
    return firstParagraph.trim();
  }

  // Find last sentence ending within maxChars
  const truncated = firstParagraph.substring(0, maxChars);
  const lastPeriod = truncated.lastIndexOf('.');

  if (lastPeriod > 0) {
    return truncated.substring(0, lastPeriod + 1).trim();
  }

  return truncated.trim() + '...';
};

/**
 * Extract section content from markdown by heading
 * @param {string} markdown - Full markdown content
 * @param {string} headingText - Section heading to find
 * @returns {string} Section content (text only, no heading)
 */
export const extractSection = (markdown, headingText) => {
  if (!markdown) return '';

  // Find the heading (case insensitive, flexible with # count)
  const headingRegex = new RegExp(`^#{1,6}\\s+${headingText}\\s*$`, 'im');
  const match = markdown.match(headingRegex);

  if (!match) return '';

  // Get content from heading to next heading or end
  const startIndex = match.index + match[0].length;
  const remaining = markdown.substring(startIndex);

  // Find next heading
  const nextHeadingMatch = remaining.match(/^#{1,6}\s+.+$/m);
  const endIndex = nextHeadingMatch ? nextHeadingMatch.index : remaining.length;

  const sectionContent = remaining.substring(0, endIndex).trim();

  // Return first 1-2 sentences
  return extractFirstSentences(sectionContent, 300);
};

/**
 * Build HowTo structured data for care guides
 * @param {Object} params - Care guide data
 * @param {string} params.commonName - Common name of species
 * @param {string} params.scientificName - Scientific name (Genus species)
 * @param {string} params.description - Guide description/excerpt
 * @param {string} params.imageUrl - Absolute URL to primary image
 * @param {string} params.markdown - Full markdown content
 * @param {string} params.url - Canonical URL of the guide
 * @returns {Object|null} HowTo schema object or null if insufficient data
 */
export const buildHowToSchema = ({
  commonName,
  scientificName,
  description,
  imageUrl,
  markdown,
  url
}) => {
  if (!isSchemaEnabled()) {
    return null;
  }

  if (!markdown || !scientificName) {
    return null;
  }

  // Define canonical sections in priority order
  const sectionMappings = [
    { name: 'Housing Requirements', patterns: ['Housing Requirements', 'Enclosure', 'Habitat Setup', 'Tank Setup'] },
    { name: 'Substrate & Environment', patterns: ['Substrate', 'Environment', 'Substrate & Environment', 'Setup'] },
    { name: 'Temperature & Humidity', patterns: ['Temperature', 'Humidity', 'Climate', 'Temperature & Humidity'] },
    { name: 'Diet & Feeding', patterns: ['Diet', 'Feeding', 'Food', 'Nutrition'] },
    { name: 'Water Requirements', patterns: ['Water', 'Water Quality', 'Hydration'] },
    { name: 'Breeding', patterns: ['Breeding', 'Reproduction', 'Eggs', 'Tadpoles'] },
    { name: 'Health & Care', patterns: ['Health', 'Common Issues', 'Diseases', 'Care Notes'] },
    { name: 'Special Notes', patterns: ['Special Notes', 'Additional Information', 'Important Notes'] }
  ];

  // Extract steps from markdown
  const steps = [];

  for (const section of sectionMappings) {
    let sectionText = '';

    // Try each pattern until we find content
    for (const pattern of section.patterns) {
      sectionText = extractSection(markdown, pattern);
      if (sectionText) break;
    }

    if (sectionText && sectionText.length > 20) { // Minimum viable content
      steps.push({
        "@type": "HowToStep",
        "name": section.name,
        "text": sectionText
      });
    }
  }

  // Require at least 2 steps for valid HowTo
  if (steps.length < 2) {
    return null;
  }

  const displayName = commonName
    ? `${commonName} (${scientificName})`
    : scientificName;

  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": `How to Care for ${displayName}`,
    "description": description || `Complete care guide for ${displayName} including habitat setup, feeding, temperature, humidity, and health tips.`,
    "image": imageUrl || "https://feelingfroggy.llc/logos/ff_original_96px.png",
    "url": url,
    "step": steps,
    "author": {
      "@type": "Organization",
      "name": "Feeling Froggy"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Feeling Froggy",
      "logo": {
        "@type": "ImageObject",
        "url": "https://feelingfroggy.llc/logos/ff_original_96px.png"
      }
    }
  };
};

/**
 * Build FAQPage structured data for care guides
 * @param {Object} params - Care guide data
 * @param {string} params.scientificName - Scientific name (Genus species)
 * @param {string} params.commonName - Common name of species (optional)
 * @param {Array} params.faqs - Array of FAQ objects with question and answer
 * @returns {Object|null} FAQPage schema object or null if insufficient data
 */
export const buildFAQPageSchema = ({
  scientificName,
  commonName,
  faqs = []
}) => {
  if (!isSchemaEnabled()) {
    return null;
  }

  if (!scientificName || !faqs || faqs.length === 0) {
    return null;
  }

  // Filter out any invalid FAQs (missing question or answer)
  const validFaqs = faqs.filter(faq =>
    faq &&
    faq.question &&
    faq.answer &&
    faq.question.trim() &&
    faq.answer.trim()
  );

  if (validFaqs.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": validFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};
